/* eslint-disable */
import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { Moment } from 'moment';
import { instance } from 'api/api.config';
import { criteriaWithComplexValues } from 'configs/searchCriteria';
import { CriteriasType, ModeType, SP, SPWithDate, TemplateType } from './search.types';
import { DefaultCriteriasKeysResponse, SearchFilterHashResponse } from './namespaces/responses';
import { getCriteriaWithComplexValues, removeFromComplexCriteria } from './utils';
import { getPayloadForTemplate } from '../../utils/convertDataForSearchFilters/convertData';
import { getTokens } from 'utils/tokens';
import { PeriodsType } from 'store/reports/reports.types';
import { Dayjs } from 'dayjs';

/**
 * @deprecated
 */
export const getAllTemplates = createAsyncThunk('template/getAllTemplates', async (payload, thunkAPI) => {
	try {
		const { token } = getTokens();
		const response = await instance.get('search_filters/', { headers: { Authorization: `Bearer ${token}` } });
		thunkAPI.dispatch(searchSlice.actions.setAllTemplates(response.data));
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

export const getTemplate = createAsyncThunk('template/getTemplate', async (payload: string, thunkAPI) => {
	try {
		const { token } = getTokens();
		const response = await instance.get(`search_filter/${payload}`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		thunkAPI.dispatch(searchSlice.actions.setCurrentTemplate(response.data));
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

export const createTemplate = createAsyncThunk(
	'template/createTemplate',
	async (payload: { title: string; items: any[] }, thunkAPI) => {
		try {
			const requestBody = getPayloadForTemplate(payload);
			const { token } = getTokens();
			const { data } = await instance.post('search_filter', requestBody, {
				headers: { Authorization: `Bearer ${token}` },
			});
			await thunkAPI.dispatch(getTemplate(data));
			thunkAPI.dispatch(getAllTemplates());
			return data;
		} catch (e: any) {
			return thunkAPI.rejectWithValue({ error: e.message });
		}
	},
);

export const updateTemplate = createAsyncThunk('template/updateTemplate', async (payload: TemplateType, thunkAPI) => {
	try {
		const { token } = getTokens();
		const { data } = await instance.put(`search_filter/${payload.id}`, payload, {
			headers: { Authorization: `Bearer ${token}` },
		});
		await thunkAPI.dispatch(getTemplate(data));
		thunkAPI.dispatch(getAllTemplates());
		return data;
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

export const deleteTemplate = createAsyncThunk('template/updateTemplate', async (payload: string, thunkAPI) => {
	try {
		const { token } = getTokens();
		const response = await instance.delete(`search_filter/${payload}`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		thunkAPI.dispatch(getAllTemplates());
		return response.data.title;
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

/**
 * @deprecated
 */
export const getAllSearchCriterias = createAsyncThunk('search/getAllSearchCriterias', async (payload, thunkAPI) => {
	try {
		const { token } = getTokens();
		const { data } = await instance.get('search_criterias/', { headers: { Authorization: `Bearer ${token}` } });
		thunkAPI.dispatch(searchSlice.actions.setAllCriterias(data));
		return data;
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

/**
 * @deprecated
 */
export const getDefaultCriterias = createAsyncThunk('search/getBaseSearchCriterias', async (_, thunkAPI) => {
	try {
		const { token } = getTokens();
		const { data } = await instance.get('search_criterias/default_keys', {
			headers: { Authorization: `Bearer ${token}` },
		});
		return data;
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

export const getSearchHash = createAsyncThunk('search/getSearchHash', async (hashKey: string, thunkAPI) => {
	try {
		const { token } = getTokens();
		const { data } = await instance.get<SearchFilterHashResponse>(`/search_filter_hash/${hashKey}`, {
			headers: { Authorization: `Bearer ${token}` },
		});
		return data;
	} catch (e: any) {
		return thunkAPI.rejectWithValue({ error: e.message });
	}
});

type InitialStateType = {
	calls: {
		mode: ModeType;
		period: PeriodsType | null | string;
		date: (Dayjs | null)[];
		activeCriterias: CriteriasType[];
		defaultCriterias: CriteriasType[];
		allTemplates: TemplateType[];
		currentTemplate: TemplateType | null;
	};
	reports: {
		activeCriterias: CriteriasType[];
		defaultCriterias: CriteriasType[];
		search_filter_hash?: string | null;
	};
	checklists: {
		activeCriterias: CriteriasType[];
		defaultCriterias: CriteriasType[];
	};
	callsFromReportCell: {
		activeCriterias: CriteriasType[];
		defaultCriterias: CriteriasType[];
	};
	deals: {
		activeCriterias: CriteriasType[];
		defaultCriterias: CriteriasType[];
	};
	allCriterias: CriteriasType[];
	allCriteriasLoading: boolean;
	defaultCriteriasKeys: DefaultCriteriasKeysResponse;
};

const initialState: InitialStateType = {
	calls: {
		mode: 'LOADING',
		period: 'today',
		date: [null, null],
		activeCriterias: [],
		defaultCriterias: [],
		allTemplates: [],
		currentTemplate: null,
	},
	reports: {
		activeCriterias: [],
		defaultCriterias: [],
		search_filter_hash: null,
	},
	checklists: {
		activeCriterias: [],
		defaultCriterias: [],
	},
	callsFromReportCell: {
		activeCriterias: [],
		defaultCriterias: [],
	},
	deals: {
		activeCriterias: [],
		defaultCriterias: [],
	},
	allCriterias: [],
	allCriteriasLoading: false,
	defaultCriteriasKeys: [],
};

export const searchSlice = createSlice({
	name: 'search',
	initialState,
	reducers: {
		// общее
		setAllCriterias(state, action: PayloadAction<CriteriasType[]>) {
			state.allCriterias = action.payload;
		},

		setDate(state, action: PayloadAction<(Dayjs | null)[]>) {
			state.calls.date = action.payload;
		},

		setPeriod(state, action: PayloadAction<PeriodsType | null | string>) {
			state.calls.period = action.payload;
		},

		setActiveCriteria(state, action: PayloadAction<SP<CriteriasType>>) {
			const { page, data } = action.payload;
			const existingCriteriaIndex = state[page].activeCriterias.findIndex(
				(item) => item.key === data.key && item.conditionItemId === data.conditionItemId,
			);
			if (existingCriteriaIndex !== -1) {
				// Обновляем существующий критерий
				state[page].activeCriterias[existingCriteriaIndex] = {
					...state[page].activeCriterias[existingCriteriaIndex],
					...data,
				};
			} else {
				// Добавляем новый критерий
				state[page].activeCriterias.push(data);
			}
		},

		setActiveCriterias(state, action: PayloadAction<SP<CriteriasType[]>>) {
			state[action.payload.page].activeCriterias = action.payload.data;
		},

		setDefaultCriterias(state, action: PayloadAction<SP<CriteriasType[]>>) {
			state[action.payload.page].defaultCriterias = action.payload.data;
		},

		setActiveCriteriaValues(state, action: PayloadAction<SP<CriteriasType>>) {
			const { page, data } = action.payload;
			const existingCriteriaIndex = state[page].activeCriterias.findIndex((item) => item.key === data.key);

			if (criteriaWithComplexValues.includes(data.key)) {
				state[page].activeCriterias = getCriteriaWithComplexValues(
					data.key,
					current(state[page].activeCriterias),
					data,
				);
			} else if (existingCriteriaIndex !== -1) {
				// Критерий существует, обновляем его значения
				state[page].activeCriterias[existingCriteriaIndex].values = data.values;
			} else {
				// Критерий не существует, добавляем его
				state[page].activeCriterias.push(data);
			}
		},

		removeActiveCriteria(state, action: PayloadAction<SP<CriteriasType>>) {
			state[action.payload.page].activeCriterias = state[action.payload.page].activeCriterias.filter(
				(item) => item.key !== action.payload.data.key,
			);
		},

		removeComplexCriteria(state, action: PayloadAction<SP<CriteriasType>>) {
			const { data, page } = action.payload;

			state[page].activeCriterias = removeFromComplexCriteria(
				current(state[page].activeCriterias),
				data.conditionItemId,
				data.key,
			);
		},

		removeAllActiveCriterias(state, action: PayloadAction<SP<null>>) {
			state[action.payload.page].activeCriterias = [];
		},

		// только для звонков
		setMode(state, action: PayloadAction<ModeType>) {
			state.calls.mode = action.payload;
		},

		setAllTemplates(state, action: PayloadAction<TemplateType[]>) {
			state.calls.allTemplates = action.payload;
		},

		setCurrentTemplate(state, action: PayloadAction<TemplateType | null>) {
			state.calls.currentTemplate = action.payload;
		},

		setDefaultCriteriasKeys(state, action: PayloadAction<DefaultCriteriasKeysResponse>) {
			state.defaultCriteriasKeys = action.payload;
		},

		setReportSearchFilterHash(state, action: PayloadAction<string>) {
			state.reports.search_filter_hash = action.payload;
		},

		searchReset: () => initialState,
	},
	extraReducers: {
		[getAllSearchCriterias.pending.type]: (state) => {
			state.allCriteriasLoading = true;
		},
		[getAllSearchCriterias.fulfilled.type]: (state) => {
			state.allCriteriasLoading = false;
		},
		[getAllSearchCriterias.rejected.type]: (state) => {
			state.allCriteriasLoading = false;
		},
	},
});
